import styled, { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    line-height: 1.5;
    scroll-behavior: smooth;
  }

  body {
    font-family: ${theme.typography.fontFamily.primary};
    background: ${theme.colors.background};
    color: ${theme.colors.textPrimary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    position: relative;
    line-height: ${theme.typography.lineHeight.normal};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.typography.fontFamily.display};
    font-weight: ${theme.typography.fontWeight.semibold};
    line-height: ${theme.typography.lineHeight.tight};
    letter-spacing: ${theme.typography.letterSpacing.tight};
    color: ${theme.colors.textPrimary};
    margin: 0;
  }

  h1 {
    font-size: ${theme.typography.fontSize['5xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.typography.fontSize['3xl']};
    }
  }

  h2 {
    font-size: ${theme.typography.fontSize['3xl']};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }

  h3 {
    font-size: ${theme.typography.fontSize['2xl']};
  }

  h4 {
    font-size: ${theme.typography.fontSize.xl};
  }

  h5 {
    font-size: ${theme.typography.fontSize.lg};
  }

  h6 {
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  p {
    line-height: ${theme.typography.lineHeight.relaxed};
    color: ${theme.colors.textSecondary};
    margin: 0;
  }

  a {
    color: ${theme.colors.textAccent};
    text-decoration: none;
    transition: all ${theme.transitions.fast};

    &:hover {
      color: ${theme.colors.accent[600]};
    }

    &:focus {
      outline: none;
      box-shadow: ${theme.shadows.focus};
      border-radius: ${theme.borderRadius.sm};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
  }

  input, textarea, select {
    font-family: inherit;
  }

  /* Professional scrollbar styles */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.primary[100]};
    border-radius: ${theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[400]};
    border-radius: ${theme.borderRadius.full};
    transition: all ${theme.transitions.base};

    &:hover {
      background: ${theme.colors.accent[500]};
    }
  }

  ::-webkit-scrollbar-corner {
    background: ${theme.colors.primary[100]};
  }

  /* Professional focus styles */
  *:focus {
    outline: none;
    box-shadow: ${theme.shadows.focusRing};
  }

  /* Focus visible for keyboard navigation */
  *:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focusRing};
  }

  /* Professional animation classes */
  .fade-in {
    animation: fadeIn 0.5s ease-out;
  }

  .fade-in-up {
    animation: fadeInUp 0.6s ease-out;
  }

  .slide-up {
    animation: slideUp 0.4s ease-out;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  /* Professional keyframe animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(79, 70, 229, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(79, 70, 229, 0.6), 0 0 30px rgba(79, 70, 229, 0.4);
    }
  }
`;

// AI-Enhanced styled components
export const Container = styled.div`
  max-width: ${theme.layout.containerMaxWidth};
  margin: 0 auto;
  padding: 0 ${theme.spacing[6]};
  position: relative;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[8]};
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    padding: 0 ${theme.spacing[12]};
  }
`;

export const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.base};
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  padding: ${theme.spacing[6]};

  &:hover {
    border-color: ${theme.colors.borderHover};
    box-shadow: ${theme.shadows.md};
    transform: translateY(-1px);
  }
`;

export const Button = styled.button<{
  $variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  text-decoration: none;
  cursor: pointer;
  position: relative;

  /* Size variants */
  ${({ $size = 'md' }) => {
    switch ($size) {
      case 'sm':
        return `
          padding: ${theme.spacing[2]} ${theme.spacing[3]};
          font-size: ${theme.typography.fontSize.xs};
        `;
      case 'lg':
        return `
          padding: ${theme.spacing[4]} ${theme.spacing[6]};
          font-size: ${theme.typography.fontSize.base};
        `;
      default:
        return `
          padding: ${theme.spacing[3]} ${theme.spacing[4]};
        `;
    }
  }}

  /* Color variants */
  ${({ $variant = 'primary' }) => {
    switch ($variant) {
      case 'secondary':
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.border};

          &:hover:not(:disabled) {
            background: ${theme.colors.surfaceHover};
            color: ${theme.colors.textPrimary};
            border-color: ${theme.colors.borderHover};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.primary[500]};
          border: 1px solid ${theme.colors.primary[500]};

          &:hover:not(:disabled) {
            background: ${theme.colors.primary[500]};
            color: white;
            box-shadow: ${theme.shadows.base};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.textSecondary};
          border: 1px solid transparent;

          &:hover:not(:disabled) {
            background: ${theme.colors.surfaceHover};
            color: ${theme.colors.textPrimary};
          }
        `;
      default:
        return `
          background: ${theme.colors.primary[500]};
          color: white;
          border: 1px solid ${theme.colors.primary[500]};
          font-weight: ${theme.typography.fontWeight.semibold};

          /* color must be restated: when this renders as <Link> the global
             a:hover rule outranks the base class colour and turns the label
             navy-on-navy. */
          &:hover:not(:disabled) {
            background: ${theme.colors.primary[600]};
            color: white;
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.base};
          }

          &:visited,
          &:active,
          &:focus {
            color: white;
          }
        `;
    }
  }}

  /* Full width */
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  /* Focus state */
  &:focus {
    outline: none;
    box-shadow: ${theme.shadows.focusRing};
  }
`;

export const Input = styled.input<{
  $hasError?: boolean;
}>`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${({ $hasError }) => $hasError ? theme.colors.error[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:hover:not(:disabled) {
    border-color: ${theme.colors.borderHover};
  }

  &:disabled {
    background: ${theme.colors.primary[200]};
    color: ${theme.colors.textDisabled};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

export const Select = styled.select<{
  $hasError?: boolean;
}>`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${({ $hasError }) => $hasError ? theme.colors.error[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:hover:not(:disabled) {
    border-color: ${theme.colors.borderHover};
  }

  &:disabled {
    background: ${theme.colors.primary[200]};
    color: ${theme.colors.textDisabled};
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    background: ${theme.colors.surface};
    color: ${theme.colors.textPrimary};
  }
`;

export const Label = styled.label`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[2]};
`;

export const ErrorMessage = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.error};
  margin-top: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  
  &::before {
    content: '⚠';
    font-size: ${theme.typography.fontSize.base};
  }
`;

export const LoadingSpinner = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  border: 2px solid ${theme.colors.primary[200]};
  border-top: 2px solid ${theme.colors.accent[500]};
  border-radius: ${theme.borderRadius.full};
  animation: spin 1s linear infinite;

  ${({ size = 'md' }) => {
    switch (size) {
      case 'sm':
        return 'width: 16px; height: 16px;';
      case 'lg':
        return 'width: 32px; height: 32px; border-width: 3px;';
      default:
        return 'width: 24px; height: 24px;';
    }
  }}
`;

// Professional status indicator component
export const StatusBadge = styled.div<{ status: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};

  ${({ status }) => {
    switch (status) {
      case 'success':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `;
      case 'error':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `;
      case 'info':
        return `
          background: ${theme.colors.info[100]};
          color: ${theme.colors.info[700]};
        `;
      default:
        return '';
    }
  }}

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${theme.borderRadius.full};
    background-color: currentColor;
  }
`;

// Professional progress indicator
export const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 8px;
  background: ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ progress }) => Math.min(Math.max(progress, 0), 100)}%;
    background: ${theme.colors.accent[500]};
    border-radius: inherit;
    transition: width ${theme.transitions.base};
  }
`;